/**
 * Cadastro por conversa ligado ao OQ: pega o cliente do AuthContext (ou da
 * prop `client`), decide se aparece, cria a conta via /createPlayer e faz o
 * login no fim.
 *
 * Para usar em outra tela (sempre deslogada), basta renderizar <AiSignup />.
 * `offsetY` afasta a isca da borda de baixo quando a tela já tem algo fixo lá.
 * Telas que abrem o chat por conta própria (ex.: landing business) usam
 * `openRequest` + `launcherOnlyAfterStart`.
 */
import { Markup } from 'interweave';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Notify } from '../../../components/Notify';
import * as routes from '../../../constants/Routes';
import { useAuth } from '../../../hooks/useAuth';
import { createAiSignupApi, getClientTerms } from '../api';
import { PRIVACY_POLICY_URL, isAiSignupAvailable } from '../utils';
import { SignupPayload, SignupPrefill } from '../signupFlow';
import { AiSignupWidget } from './AiSignupWidget';

interface AiSignupProps {
  offsetX?: number;
  offsetY?: number;
  /** Atalho "Ir para o login". Padrão: abre a tela de login. */
  onLogin?: () => void;
  /** Cliente da página, quando não é o do AuthContext (ex.: landing business). */
  client?: any;
  /** Dados já informados na página; o chat não pergunta de novo. */
  prefill?: SignupPrefill;
  /** Indicador do novo player, quando a página sabe quem é. */
  hostId?: string;
  /** Abre o chat sempre que este número muda (e é maior que zero). */
  openRequest?: number;
  /** Esconde a isca até a conversa começar. */
  launcherOnlyAfterStart?: boolean;
}

type TermsState =
  | { status: 'loading' }
  | { status: 'ready'; html: string; image?: string }
  | { status: 'error' };

export function AiSignup({
  offsetX,
  offsetY,
  onLogin,
  client: clientProp,
  prefill,
  hostId,
  openRequest,
  launcherOnlyAfterStart,
}: AiSignupProps) {
  const navigate = useNavigate();
  const { client: authClient, signed, signIn, setClient } = useAuth();
  const client = clientProp || authClient;
  const [terms, setTerms] = useState<TermsState | null>(null);

  // Guarda e-mail e senha só até o login automático depois do cadastro.
  const credentialsRef = useRef<SignupPayload | null>(null);

  const signupApi = useMemo(() => {
    if (!client) return null;
    const base = createAiSignupApi(client, { hostId });
    return {
      ...base,
      register: async (payload: SignupPayload) => {
        const created = await base.register(payload);
        credentialsRef.current = payload;
        // O login (e o que vem depois dele) usa o cliente do AuthContext.
        // Trocar aqui dá tempo do contexto atualizar antes do onSuccess.
        if (clientProp && authClient?.id !== clientProp.id) {
          setClient(clientProp);
        }
        return created;
      },
    };
  }, [client, clientProp, authClient, hostId, setClient]);

  if (signed || !signupApi || !isAiSignupAvailable(client)) return null;

  const handleSuccess = async () => {
    const credentials = credentialsRef.current;
    credentialsRef.current = null;
    if (!credentials) return;

    if (client.mgmVinculated === 'true') {
      sessionStorage.removeItem('clientId');
      sessionStorage.removeItem('playerId');
      sessionStorage.removeItem('action');
    }

    try {
      await signIn({
        email: credentials.email,
        password: credentials.password,
        clientId: client.id,
      });
      navigate(
        client.clientUrl === 'bomnegocio'
          ? routes.REGISTER_DETAILS
          : routes.HOME
      );
    } catch (error) {
      console.log('AiSignup signIn error', error);
      Notify('Conta criada! Entre com seu e-mail e senha.', 'success');
    }
  };

  const openTerms = () => {
    setTerms({ status: 'loading' });
    getClientTerms(client.id)
      .then((data) =>
        setTerms(
          data?.htmlString
            ? {
                status: 'ready',
                html: data.htmlString,
                image: data.termsImage || undefined,
              }
            : { status: 'error' }
        )
      )
      .catch(() => setTerms({ status: 'error' }));
  };

  const renderTerms = () => {
    if (!terms) return null;
    if (terms.status === 'loading') return <p>Carregando os Termos de Uso…</p>;
    if (terms.status === 'error')
      return <p>Não consegui carregar os Termos de Uso. Tente de novo.</p>;
    return (
      <>
        {terms.image && <img src={terms.image} alt="" />}
        <Markup content={terms.html} />
      </>
    );
  };

  return (
    <AiSignupWidget
      api={signupApi}
      brandName={client.companyName}
      primaryColor={client.primaryColor}
      privacyUrl={PRIVACY_POLICY_URL}
      offsetX={offsetX}
      offsetY={offsetY}
      prefill={prefill}
      openRequest={openRequest}
      launcherOnlyAfterStart={launcherOnlyAfterStart}
      onSuccess={handleSuccess}
      onLogin={onLogin || (() => navigate(routes.START))}
      onOpenDoc={(doc) => {
        // A Política é um PDF e abre em outra aba; os Termos são por cliente
        // e abrem dentro do chat.
        if (doc !== 'termos') return false;
        openTerms();
        return true;
      }}
      overlay={terms ? renderTerms() : null}
      onCloseOverlay={() => setTerms(null)}
    />
  );
}
